import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';

/** Shape of a parent topic from the domain-overview share response */
interface SharedParentTopic {
  parentTopicSlug: string;
  label: string;
  childTopicSlugs: string[];
  pageCount: number;
  effectivePageCountForCoverage?: number;
  totalWeight: number;
  averageWeight: number;
  coverageScore: number;
  breadthScore: number;
  depthScore: number;
  strengthScore: number;
  focusScore: number;
  authorityScore: number;
  topicTier?: string;
  topicTierReasoning?: string[];
  contextualLinkCount?: number;
  structuralLinkCount?: number;
  linkScore?: number;
  authorityExplanation?: {
    coverage?: string;
    strength?: string;
    focus?: string;
    links?: string;
  };
}

/** domain-overview resource shape */
interface DomainOverviewData {
  resourceType: 'domain-overview';
  domain: string;
  label: string | null;
  parentTopics: SharedParentTopic[];
}

/** Incoming link stats shape */
interface IncomingLinkStatsEntry {
  contextual: number;
  structural: number;
  total: number;
}

/** Domain topic authority entry */
interface DomainTopicEntry {
  parentTopicSlug: string;
  label: string;
  childTopicSlugs: string[];
  topicTier?: string;
  authorityScore: number;
}

/** Domain topics bundle */
interface DomainTopicsBundle {
  domain: string | null;
  parentTopics: DomainTopicEntry[];
}

/** Topic share coverage domain entry */
interface TopicShareDomainCoverage {
  domain: string;
  entitiesCovered: number;
  totalEntities: number;
  coveragePercentage: number;
  topUrls: { url: string; title?: string; entitiesCount: number }[];
}

/** Topic share coverage data */
interface TopicShareCoverageBundle {
  available: boolean;
  topic: string;
  domains: TopicShareDomainCoverage[];
  sharedEntitiesCount: number;
  lastUpdated: string | null;
}

/** comparison resource shape */
interface ComparisonData {
  resourceType: 'comparison';
  label: string | null;
  execution: {
    id: string;
    yourPageUrl: string;
    competitorPageUrl: string;
    mode: string;
    steps: any[];
    totalCost: number;
    startedAt: string;
    completedAt: string | null;
  };
  yourPage: { url: string; analysis: any | null };
  competitorPage: { url: string; analysis: any | null };
  // Additional data bundled for full diagnosis parity
  incomingLinkStats?: {
    yourPage: IncomingLinkStatsEntry;
    competitorPage: IncomingLinkStatsEntry;
  };
  domainTopics?: {
    yourDomain: DomainTopicsBundle;
    competitorDomain: DomainTopicsBundle;
  };
  topicShareCoverage?: TopicShareCoverageBundle | null;
}

/** page-analysis resource shape */
interface PageAnalysisData {
  resourceType: 'page-analysis';
  label: string | null;
  url: string | null;
  analysis: any;
}

type ShareData = DomainOverviewData | ComparisonData | PageAnalysisData;

interface ShareResponse {
  success: boolean;
  data: ShareData;
  timestamp: string;
}

/** Keyword overlap types (mirrors page-comparison logic) */
interface SharedKeyword {
  keyword: string;
  yourPosition: number;
  competitorPosition: number;
  searchVolume?: number;
}

interface UniqueKeyword {
  keyword: string;
  position: number;
  searchVolume?: number;
}

interface KeywordOverlapAnalysis {
  sharedKeywords: SharedKeyword[];
  yourUniqueKeywords: UniqueKeyword[];
  competitorUniqueKeywords: UniqueKeyword[];
  sharedCount: number;
  yourUniqueCount: number;
  competitorUniqueCount: number;
  sharedAvgPositionYour: number;
  sharedAvgPositionCompetitor: number;
}

type TopicTier = 'owned' | 'aspirational' | 'ambient' | 'adjacent' | 'anti' | 'unclassified' | null;

interface DiagnosisBullet {
  text: string;
  category: 'keywords' | 'links' | 'freshness' | 'content' | 'topicAuthority';
  winner: 'you' | 'competitor';
  priority: number;
}

@Component({
  selector: 'app-share-viewer',
  standalone: true,
  imports: [CommonModule, RouterModule],
  templateUrl: './share-viewer.html',
  styleUrl: './share-viewer.css',
})
export class ShareViewerComponent implements OnInit {
  private readonly API_URL = environment.apiUrl + '/v1';

  loading = true;
  error: string | null = null;
  shareData: ShareData | null = null;

  // Cached computation results
  private _keywordOverlapCache: KeywordOverlapAnalysis | null | undefined = undefined;
  private _diagnosisCache: DiagnosisBullet[] | null = null;

  // Topic share URLs expand state
  topicShareUrlsExpanded = false;

  constructor(
    private route: ActivatedRoute,
    private http: HttpClient,
  ) {}

  ngOnInit() {
    const token = this.route.snapshot.paramMap.get('token');
    if (!token) {
      this.loading = false;
      this.error = 'No share token provided';
      return;
    }
    this.loadShareData(token);
  }

  private loadShareData(token: string) {
    this.loading = true;
    this.error = null;
    this.http.get<ShareResponse>(`${this.API_URL}/share/${token}`).subscribe({
      next: (res) => {
        this.loading = false;
        if (res.success && res.data) {
          this.shareData = res.data;
        } else {
          this.error = 'Unable to load shared content';
        }
      },
      error: (err) => {
        this.loading = false;
        if (err.status === 404) {
          this.error = 'This share link is invalid, expired, or has been revoked.';
        } else {
          this.error = err.error?.message || 'Failed to load shared content. Please try again.';
        }
      },
    });
  }

  // ═══════════════════════════════════════════════════════════
  // Getters for resource types
  // ═══════════════════════════════════════════════════════════

  get domainData(): DomainOverviewData | null {
    return this.shareData?.resourceType === 'domain-overview' ? (this.shareData as DomainOverviewData) : null;
  }

  get comparisonData(): ComparisonData | null {
    return this.shareData?.resourceType === 'comparison' ? (this.shareData as ComparisonData) : null;
  }

  get pageAnalysisData(): PageAnalysisData | null {
    return this.shareData?.resourceType === 'page-analysis' ? (this.shareData as PageAnalysisData) : null;
  }

  // ═══════════════════════════════════════════════════════════
  // Helpers for domain-overview
  // ═══════════════════════════════════════════════════════════

  getScoreColor(score: number): string {
    if (score >= 80) return '#10b981';
    if (score >= 60) return '#3b82f6';
    if (score >= 40) return '#f59e0b';
    return '#ef4444';
  }

  getTierLabel(tier?: string): string {
    switch (tier) {
      case 'owned': return '🏆 Owned';
      case 'aspirational': return '📈 Aspirational';
      case 'ambient': return '💬 Ambient';
      case 'adjacent': return '🔗 Adjacent';
      case 'anti': return '🚫 Filtered';
      default: return '❓ Unclassified';
    }
  }

  getTierClass(tier?: string): string {
    switch (tier) {
      case 'owned': return 'tier-owned';
      case 'aspirational': return 'tier-aspirational';
      case 'ambient': return 'tier-ambient';
      case 'adjacent': return 'tier-adjacent';
      case 'anti': return 'tier-anti';
      default: return 'tier-unclassified';
    }
  }

  getScoreBarWidth(score: number): string {
    return `${Math.min(score, 100)}%`;
  }

  // ═══════════════════════════════════════════════════════════
  // Helpers for page analysis data access
  // (PageAnalysis documents use top-level fields, not nested)
  // ═══════════════════════════════════════════════════════════

  getPageTitle(analysis: any): string {
    if (!analysis) return 'N/A';
    return analysis.rawData?.pageData?.meta?.title ||
           analysis.rawData?.summary?.url ||
           'Untitled';
  }

  getOnPageScore(analysis: any): number | null {
    return analysis?.onpageScore ?? null;
  }

  getWordCount(analysis: any): number | null {
    return analysis?.wordCount ?? null;
  }

  getStatusCode(analysis: any): number | null {
    return analysis?.statusCode ?? null;
  }

  getTopicSignalsCount(analysis: any): number {
    return analysis?.topic?.topicSignals?.length || 0;
  }

  getTopicSignals(analysis: any): any[] {
    return analysis?.topic?.topicSignals || [];
  }

  getInternalLinksCount(analysis: any): number {
    return analysis?.links?.internalLinks?.length || 0;
  }

  getContextualLinksCount(analysis: any): number {
    const links: any[] = analysis?.links?.internalLinks || [];
    return links.filter((l: any) => l.linkType === 'contextual').length;
  }

  getStructuralLinksCount(analysis: any): number {
    const links: any[] = analysis?.links?.internalLinks || [];
    return links.filter((l: any) => l.linkType === 'structural').length;
  }

  getRankedKeywords(analysis: any): any[] {
    const keywords = analysis?.rankedKeywords?.keywords || [];
    return [...keywords].sort((a: any, b: any) => (a.position || 999) - (b.position || 999));
  }

  getRankedKeywordsCount(analysis: any): number {
    return analysis?.rankedKeywords?.keywords?.length || 0;
  }

  getCriticalIssues(analysis: any): string[] {
    return analysis?.criticalIssues || [];
  }

  getWarnings(analysis: any): string[] {
    return analysis?.warnings || [];
  }

  getReadabilityIndex(analysis: any): number | null {
    return analysis?.readabilityIndex ?? null;
  }

  getH1Count(analysis: any): number { return analysis?.h1Count ?? 0; }
  getH2Count(analysis: any): number { return analysis?.h2Count ?? 0; }
  getH3Count(analysis: any): number { return analysis?.h3Count ?? 0; }

  getLastContentModified(analysis: any): string | null {
    return analysis?.lastContentModified || null;
  }

  getLastContentModifiedSource(analysis: any): string | null {
    return analysis?.lastContentModifiedSource || null;
  }

  // ═══════════════════════════════════════════════════════════
  // Keyword Overlap Analysis (mirrors page-comparison logic)
  // ═══════════════════════════════════════════════════════════

  computeKeywordOverlap(): KeywordOverlapAnalysis | null {
    if (this._keywordOverlapCache !== undefined) return this._keywordOverlapCache;

    const data = this.comparisonData;
    if (!data?.yourPage?.analysis || !data?.competitorPage?.analysis) {
      this._keywordOverlapCache = null;
      return null;
    }

    const yourKeywords = this.getRankedKeywords(data.yourPage.analysis);
    const competitorKeywords = this.getRankedKeywords(data.competitorPage.analysis);

    if (yourKeywords.length === 0 && competitorKeywords.length === 0) {
      this._keywordOverlapCache = null;
      return null;
    }

    const yourMap = new Map<string, any>();
    yourKeywords.forEach((k: any) => {
      const n = k.keyword?.toLowerCase().trim();
      if (n) yourMap.set(n, k);
    });

    const competitorMap = new Map<string, any>();
    competitorKeywords.forEach((k: any) => {
      const n = k.keyword?.toLowerCase().trim();
      if (n) competitorMap.set(n, k);
    });

    const shared: SharedKeyword[] = [];
    const yourUnique: UniqueKeyword[] = [];
    const competitorUnique: UniqueKeyword[] = [];

    yourMap.forEach((yourKw, normalized) => {
      const competitorKw = competitorMap.get(normalized);
      if (competitorKw) {
        shared.push({
          keyword: yourKw.keyword,
          yourPosition: yourKw.position || 999,
          competitorPosition: competitorKw.position || 999,
          searchVolume: yourKw.searchVolume || competitorKw.searchVolume,
        });
      } else {
        yourUnique.push({
          keyword: yourKw.keyword,
          position: yourKw.position || 999,
          searchVolume: yourKw.searchVolume,
        });
      }
    });

    competitorMap.forEach((competitorKw, normalized) => {
      if (!yourMap.has(normalized)) {
        competitorUnique.push({
          keyword: competitorKw.keyword,
          position: competitorKw.position || 999,
          searchVolume: competitorKw.searchVolume,
        });
      }
    });

    const sharedAvgYour = shared.length > 0
      ? shared.reduce((s, k) => s + k.yourPosition, 0) / shared.length : 0;
    const sharedAvgComp = shared.length > 0
      ? shared.reduce((s, k) => s + k.competitorPosition, 0) / shared.length : 0;

    this._keywordOverlapCache = {
      sharedKeywords: shared,
      yourUniqueKeywords: yourUnique,
      competitorUniqueKeywords: competitorUnique,
      sharedCount: shared.length,
      yourUniqueCount: yourUnique.length,
      competitorUniqueCount: competitorUnique.length,
      sharedAvgPositionYour: Math.round(sharedAvgYour * 10) / 10,
      sharedAvgPositionCompetitor: Math.round(sharedAvgComp * 10) / 10,
    };
    return this._keywordOverlapCache;
  }

  // ═══════════════════════════════════════════════════════════
  // Diagnosis Bullets (mirrors page-comparison logic)
  // ═══════════════════════════════════════════════════════════

  /**
   * Diagnosis bullets — mirrors the EXACT logic from page-comparison.ts
   * including all 5 analyzers: keywords, links (incoming), freshness, content, topic authority.
   */
  getDiagnosisBullets(): DiagnosisBullet[] {
    if (this._diagnosisCache) return this._diagnosisCache;

    const data = this.comparisonData;
    if (!data?.yourPage?.analysis || !data?.competitorPage?.analysis) return [];

    const bullets: DiagnosisBullet[] = [];
    const yourAnalysis = data.yourPage.analysis;
    const compAnalysis = data.competitorPage.analysis;

    // ── 1) Keyword advantage (priority 1 + 2) ──
    const overlap = this.computeKeywordOverlap();
    if (overlap && overlap.sharedCount >= 3) {
      const diff = overlap.sharedAvgPositionYour - overlap.sharedAvgPositionCompetitor;
      if (diff >= 3) {
        bullets.push({
          text: `They rank better on shared keywords (avg pos ${overlap.sharedAvgPositionCompetitor.toFixed(1)} vs your ${overlap.sharedAvgPositionYour.toFixed(1)}).`,
          category: 'keywords', priority: 1, winner: 'competitor',
        });
      } else if (diff <= -3) {
        bullets.push({
          text: `You rank better on shared keywords (avg pos ${overlap.sharedAvgPositionYour.toFixed(1)} vs their ${overlap.sharedAvgPositionCompetitor.toFixed(1)}).`,
          category: 'keywords', priority: 1, winner: 'you',
        });
      }
    }
    if (overlap) {
      const yU = overlap.yourUniqueCount;
      const cU = overlap.competitorUniqueCount;
      if (cU >= 5 && yU >= 1 && cU / yU >= 1.5) {
        bullets.push({
          text: `They rank for more unique keywords (${cU} vs ${yU}).`,
          category: 'keywords', priority: 2, winner: 'competitor',
        });
      }
      if (yU >= 5 && cU >= 1 && yU / cU >= 1.5) {
        bullets.push({
          text: `You rank for more unique keywords (${yU} vs ${cU}).`,
          category: 'keywords', priority: 2, winner: 'you',
        });
      }
    }

    // ── 2) Link advantage — incoming contextual links (priority 1) ──
    // Uses pre-computed incoming link stats from the backend
    const yourIncoming = data.incomingLinkStats?.yourPage?.contextual ?? 0;
    const compIncoming = data.incomingLinkStats?.competitorPage?.contextual ?? 0;

    if (yourIncoming > 0 || compIncoming > 0) {
      if (compIncoming >= 3 && yourIncoming >= 1) {
        const ratio = compIncoming / yourIncoming;
        if (ratio >= 1.5) {
          bullets.push({
            text: `They have more contextual incoming links (${compIncoming} vs ${yourIncoming}).`,
            category: 'links', priority: 1, winner: 'competitor',
          });
        }
      } else if (compIncoming >= 3 && yourIncoming === 0) {
        bullets.push({
          text: `They have contextual incoming links (${compIncoming}) while you have none.`,
          category: 'links', priority: 1, winner: 'competitor',
        });
      }

      if (yourIncoming >= 3 && compIncoming >= 1) {
        const ratio = yourIncoming / compIncoming;
        if (ratio >= 1.5) {
          bullets.push({
            text: `You have more contextual incoming links (${yourIncoming} vs ${compIncoming}).`,
            category: 'links', priority: 1, winner: 'you',
          });
        }
      } else if (yourIncoming >= 3 && compIncoming === 0) {
        bullets.push({
          text: `You have contextual incoming links (${yourIncoming}) while they have none.`,
          category: 'links', priority: 1, winner: 'you',
        });
      }
    }

    // Outgoing contextual links (priority 3)
    const yourCtx = this.getContextualLinksCount(yourAnalysis);
    const compCtx = this.getContextualLinksCount(compAnalysis);
    if (compCtx >= 5 && yourCtx >= 1 && compCtx / yourCtx >= 1.5) {
      bullets.push({
        text: `They link out to more related content (${compCtx} vs ${yourCtx} contextual links).`,
        category: 'links', priority: 3, winner: 'competitor',
      });
    }
    if (yourCtx >= 5 && compCtx >= 1 && yourCtx / compCtx >= 1.5) {
      bullets.push({
        text: `You link out to more related content (${yourCtx} vs ${compCtx} contextual links).`,
        category: 'links', priority: 3, winner: 'you',
      });
    }

    // ── 3) Freshness advantage (priority 2 — 3+ months difference) ──
    const yourMod = this.getLastContentModified(yourAnalysis);
    const compMod = this.getLastContentModified(compAnalysis);
    if (yourMod && compMod) {
      const yourDate = new Date(yourMod);
      const compDate = new Date(compMod);
      const diffMonths = (compDate.getTime() - yourDate.getTime()) / (1000 * 60 * 60 * 24 * 30);
      if (diffMonths >= 3) {
        bullets.push({
          text: `Their content is fresher (updated ${this.getMonthsAgo(compDate)} vs your ${this.getMonthsAgo(yourDate)}).`,
          category: 'freshness', priority: 2, winner: 'competitor',
        });
      } else if (diffMonths <= -3) {
        bullets.push({
          text: `Your content is fresher (updated ${this.getMonthsAgo(yourDate)} vs their ${this.getMonthsAgo(compDate)}).`,
          category: 'freshness', priority: 2, winner: 'you',
        });
      }
    }

    // ── 4) Content advantage — word count (priority 3, 30%+ more) ──
    const yourWC = this.getWordCount(yourAnalysis) || 0;
    const compWC = this.getWordCount(compAnalysis) || 0;
    if (yourWC >= 200 && compWC >= 200) {
      if (compWC > yourWC) {
        const ratio = compWC / yourWC;
        if (ratio >= 1.3) {
          bullets.push({
            text: `Their page has more comprehensive content (${compWC.toLocaleString()} vs ${yourWC.toLocaleString()} words).`,
            category: 'content', priority: 3, winner: 'competitor',
          });
        }
      } else if (yourWC > compWC) {
        const ratio = yourWC / compWC;
        if (ratio >= 1.3) {
          bullets.push({
            text: `Your page has more comprehensive content (${yourWC.toLocaleString()} vs ${compWC.toLocaleString()} words).`,
            category: 'content', priority: 3, winner: 'you',
          });
        }
      }
    }

    // ── 5) Topic authority advantage (priority 1.5 or 2) ──
    this.analyzeTopicAuthorityAdvantage(bullets);

    bullets.sort((a, b) => a.priority - b.priority);
    this._diagnosisCache = bullets;
    return bullets;
  }

  /**
   * Analyze topic authority advantages (mirrors page-comparison.ts exactly).
   * Uses domain topics data bundled in the share response.
   */
  private analyzeTopicAuthorityAdvantage(bullets: DiagnosisBullet[]): void {
    const data = this.comparisonData;
    if (!data?.yourPage?.analysis || !data?.competitorPage?.analysis) return;
    if (!data.domainTopics) return;

    // Get main topic from each page
    const yourMainTopic = this.getMainTopic(data.yourPage.analysis);
    const compMainTopic = this.getMainTopic(data.competitorPage.analysis);
    if (!yourMainTopic?.topicSlug || !compMainTopic?.topicSlug) return;

    // Find matching authority in domain topics
    const yourTopics = data.domainTopics.yourDomain?.parentTopics || [];
    const compTopics = data.domainTopics.competitorDomain?.parentTopics || [];

    const yourMatch = this.findMatchingTopic(yourMainTopic.topicSlug, yourTopics);
    const compMatch = this.findMatchingTopic(compMainTopic.topicSlug, compTopics);

    if (!yourMatch || !compMatch) return;

    const tierRanking = ['unclassified', 'anti', 'adjacent', 'ambient', 'aspirational', 'owned'];
    const yourTierIndex = yourMatch.topicTier ? tierRanking.indexOf(yourMatch.topicTier) : -1;
    const compTierIndex = compMatch.topicTier ? tierRanking.indexOf(compMatch.topicTier) : -1;
    const yourScore = yourMatch.authorityScore;
    const compScore = compMatch.authorityScore;

    const sameMainTopic = yourMainTopic.topicSlug.toLowerCase().trim() === compMainTopic.topicSlug.toLowerCase().trim();

    if (sameMainTopic) {
      const topicLabel = yourMatch.label || yourMainTopic.label;
      if (compTierIndex > yourTierIndex && compTierIndex >= 4) {
        bullets.push({
          text: `Their domain has stronger authority on "${topicLabel}" (${this.formatTopicTier(compMatch.topicTier)} vs ${this.formatTopicTier(yourMatch.topicTier)}).`,
          category: 'topicAuthority', priority: 1.5, winner: 'competitor',
        });
      } else if (yourTierIndex > compTierIndex && yourTierIndex >= 4) {
        bullets.push({
          text: `Your domain has stronger authority on "${topicLabel}" (${this.formatTopicTier(yourMatch.topicTier)} vs ${this.formatTopicTier(compMatch.topicTier)}).`,
          category: 'topicAuthority', priority: 1.5, winner: 'you',
        });
      } else if (yourTierIndex === compTierIndex && yourTierIndex >= 3) {
        const scoreDiff = compScore - yourScore;
        if (scoreDiff >= 15) {
          bullets.push({
            text: `Their domain has higher authority on "${topicLabel}" (${compScore} vs ${yourScore}).`,
            category: 'topicAuthority', priority: 2, winner: 'competitor',
          });
        } else if (scoreDiff <= -15) {
          bullets.push({
            text: `Your domain has higher authority on "${topicLabel}" (${yourScore} vs ${compScore}).`,
            category: 'topicAuthority', priority: 2, winner: 'you',
          });
        }
      }
    } else {
      const yourTopicLabel = yourMatch.label || yourMainTopic.label;
      const compTopicLabel = compMatch.label || compMainTopic.label;
      if (compTierIndex > yourTierIndex && compTierIndex >= 4) {
        bullets.push({
          text: `Their domain is ${this.formatTopicTier(compMatch.topicTier)} on "${compTopicLabel}" while yours is ${this.formatTopicTier(yourMatch.topicTier)} on "${yourTopicLabel}".`,
          category: 'topicAuthority', priority: 1.5, winner: 'competitor',
        });
      } else if (yourTierIndex > compTierIndex && yourTierIndex >= 4) {
        bullets.push({
          text: `Your domain is ${this.formatTopicTier(yourMatch.topicTier)} on "${yourTopicLabel}" while theirs is ${this.formatTopicTier(compMatch.topicTier)} on "${compTopicLabel}".`,
          category: 'topicAuthority', priority: 1.5, winner: 'you',
        });
      } else if (yourTierIndex === compTierIndex && yourTierIndex >= 3) {
        const scoreDiff = compScore - yourScore;
        if (scoreDiff >= 15) {
          bullets.push({
            text: `Their domain scores higher on their topic (${compScore} on "${compTopicLabel}") vs yours (${yourScore} on "${yourTopicLabel}").`,
            category: 'topicAuthority', priority: 2, winner: 'competitor',
          });
        } else if (scoreDiff <= -15) {
          bullets.push({
            text: `Your domain scores higher on your topic (${yourScore} on "${yourTopicLabel}") vs theirs (${compScore} on "${compTopicLabel}").`,
            category: 'topicAuthority', priority: 2, winner: 'you',
          });
        }
      }
    }
  }

  /** Get the main (highest-weight) topic from a page analysis. */
  private getMainTopic(analysis: any): { topicSlug: string; label: string } | null {
    const signals = analysis?.topic?.topicSignals || [];
    if (signals.length === 0) return null;
    const sorted = [...signals].sort((a: any, b: any) => (b.weight || 0) - (a.weight || 0));
    return { topicSlug: sorted[0].topicSlug || '', label: sorted[0].label || '' };
  }

  /** Find a matching topic in domain authority data. */
  private findMatchingTopic(topicSlug: string, topics: DomainTopicEntry[]): DomainTopicEntry | null {
    const normalized = topicSlug.toLowerCase().trim();
    return topics.find(t => {
      if (t.parentTopicSlug.toLowerCase().trim() === normalized) return true;
      if (t.childTopicSlugs?.some(c => c.toLowerCase().trim() === normalized)) return true;
      return false;
    }) || null;
  }

  /** Format topic tier label (capitalize first letter). */
  formatTopicTier(tier: string | undefined | null): string {
    if (!tier) return 'Unclassified';
    return tier.charAt(0).toUpperCase() + tier.slice(1);
  }

  /** Format date as "X months ago" or "X days ago" (matches page-comparison). */
  private getMonthsAgo(date: Date): string {
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    if (diffDays < 30) {
      return diffDays === 1 ? '1 day ago' : `${diffDays} days ago`;
    }
    const diffMonths = Math.floor(diffDays / 30);
    return diffMonths === 1 ? '1 month ago' : `${diffMonths} months ago`;
  }

  hasCompetitorAdvantages(): boolean {
    return this.getDiagnosisBullets().some(b => b.winner === 'competitor');
  }

  hasYourAdvantages(): boolean {
    return this.getDiagnosisBullets().some(b => b.winner === 'you');
  }

  // ═══════════════════════════════════════════════════════════
  // General helpers
  // ═══════════════════════════════════════════════════════════

  formatDate(dateStr: string | null): string {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  }

  formatRelativeDate(date: Date): string {
    const now = new Date();
    const diffDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 86400));
    if (diffDays < 1) return 'today';
    if (diffDays === 1) return 'yesterday';
    if (diffDays < 30) return `${diffDays} days ago`;
    if (diffDays < 365) return `${Math.floor(diffDays / 30)} months ago`;
    return `${Math.floor(diffDays / 365)} years ago`;
  }

  shortenUrl(url: string, maxLen = 60): string {
    if (!url) return '';
    if (url.length <= maxLen) return url;
    return url.substring(0, maxLen - 3) + '…';
  }

  getScoreClass(score: number | null): string {
    if (score === null) return '';
    if (score >= 70) return 'score-good';
    if (score >= 50) return 'score-fair';
    return 'score-poor';
  }

  getStatusCodeClass(code: number | null): string {
    if (code === null) return '';
    if (code >= 200 && code < 300) return 'status-ok';
    if (code >= 300 && code < 400) return 'status-redirect';
    return 'status-error';
  }

  getFreshnessLabel(source: string | null): string {
    if (!source) return '';
    const labels: Record<string, string> = {
      'visible_updated_text': 'page content',
      'json_ld_dateModified': 'structured data',
      'dataforseo_meta_tag': 'meta tag',
      'html_meta_last_modified': 'meta tag',
      'og_updated_time': 'Open Graph',
    };
    return labels[source] || source;
  }

  // ═══════════════════════════════════════════════════════════
  // Topic Share Coverage helpers
  // ═══════════════════════════════════════════════════════════

  get topicShareCoverage(): TopicShareCoverageBundle | null {
    return this.comparisonData?.topicShareCoverage || null;
  }

  hasTopicShareCoverage(): boolean {
    return !!this.topicShareCoverage?.available;
  }

  getYourDomainCoverage(): TopicShareDomainCoverage | null {
    const cov = this.topicShareCoverage;
    if (!cov?.available || !cov.domains || cov.domains.length < 1) return null;
    return cov.domains[0] || null;
  }

  getCompetitorDomainCoverage(): TopicShareDomainCoverage | null {
    const cov = this.topicShareCoverage;
    if (!cov?.available || !cov.domains || cov.domains.length < 2) return null;
    return cov.domains[1] || null;
  }

  toggleTopicShareUrls(): void {
    this.topicShareUrlsExpanded = !this.topicShareUrlsExpanded;
  }

  // ═══════════════════════════════════════════════════════════
  // Domain extraction helper
  // ═══════════════════════════════════════════════════════════

  extractDomainFromUrl(url: string): string {
    try {
      return new URL(url).hostname.replace(/^www\./, '');
    } catch {
      return url;
    }
  }
}
